import React from "react";
import { Box, Chip, Typography, Tooltip } from "@mui/material";
import { GridColDef } from "@mui/x-data-grid";
import { formatCurrency, formatDate, getStatusDisplayText, getStatusChipColors } from "../utils/claimStatusUtils";
import { StatusHelpTooltip } from "./StatusHelpTooltip";
import { StatusLegendPopover } from "./StatusLegendPopover";

/**
 * Claims Table Column Definitions
 * Extracted to follow codebase pattern of separating column definitions from table components
 *
 * Note: Formatting and status utilities are now centralized in ../utils/claimStatusUtils.ts
 */

// ============================================================================
// Column Definitions Factory
// ============================================================================

/**
 * Generate column definitions for the Claims Table
 */
export const getClaimsTableColumnDefs = (): GridColDef[] => [
  {
    field: "status",
    headerName: "Status",
    width: 180,
    renderHeader: () => (
      <Box sx={{ display: "flex", alignItems: "center" }}>
        <Typography variant="body2" sx={{ fontWeight: 600 }}>
          Status
        </Typography>
        <StatusLegendPopover />
      </Box>
    ),
    renderCell: (params) => {
      return (
        <StatusHelpTooltip status={params.value} placement="top">
          <Chip
            label={getStatusDisplayText(params.value)}
            size="small"
            sx={{
              fontWeight: 600,
              borderRadius: "8px",
              height: "32px",
              backgroundColor: getStatusChipColors(params.value).backgroundColor,
              color: getStatusChipColors(params.value).color,
              "& .MuiChip-label": {
                px: 1,
                fontSize: "0.813rem",
              },
            }}
          />
        </StatusHelpTooltip>
      );
    },
  },
  {
    field: "location",
    headerName: "Facility",
    width: 250,
    valueGetter: (params) => {
      const location = params.row.unit?.safeleaseLocationDetail;
      if (!location) return "N/A";

      return location.locationName || "N/A";
    },
  },
  {
    field: "unit",
    headerName: "Unit",
    width: 120,
    valueGetter: (params) => params.row.unit?.name || "N/A",
  },
  {
    field: "tenant",
    headerName: "Tenant",
    width: 180,
    valueGetter: (params) => {
      const user = params.row.unit?.cognitoUserDetail;
      if (!user) return "N/A";
      return `${user.userFirstName || ""} ${user.userLastName || ""}`.trim();
    },
  },
  {
    field: "cause",
    headerName: "Cause",
    width: 120,
    valueGetter: (params) => params.row.claimType?.name || "N/A",
  },
  {
    field: "lossDate",
    headerName: "Loss Date",
    width: 120,
    valueGetter: (params) => formatDate(params.row.lossDate),
  },
  {
    field: "submittedOn",
    headerName: "Submitted By Tenant",
    width: 180,
    valueGetter: (params) => formatDate(params.row.submittedOn),
  },
  {
    field: "claimAmount",
    headerName: "Claim Amount",
    width: 140,
    valueGetter: (params) => {
      // Calculate from line items
      const lineItems = params.row.lineItems || [];
      const total = lineItems.reduce((sum: number, item: any) => sum + (item.requestedAmount || 0), 0);
      return total / 100; // Convert cents to dollars
    },
    renderCell: (params) => formatCurrency(params.value),
  },
  {
    field: "coverageAmount",
    headerName: "Coverage",
    width: 130,
    valueGetter: (params) => {
      const coverage = params.row.unit?.selectedPolicyMatchData?.coverage;
      return coverage ? coverage / 100 : null; // Convert cents to dollars
    },
    renderCell: (params) => formatCurrency(params.value),
  },
];
